// File: src/components/admin/UserDetailView.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
// All rights reserved.
//
// User Detail View component for viewing and editing individual users

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, CircularProgress,
    Alert, Snackbar, Card, CardContent, Chip, TextField,
    Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import { ArrowBack, Star, Assessment, DataObject, Archive, RestoreFromTrash, CreditCard as SubscriptionIcon, Download as DownloadIcon, SmartToy, Cable as PlatformsIcon, AutoFixHigh as AutopilotIcon, Speed as RateLimitIcon, Block, DeleteForever } from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../services/adminApi';
import JsonViewTab from './common/JsonViewTab';
import AuditLogView from './AuditLogView';
import { FactCheck as AuditIcon } from '@mui/icons-material';

// Import new shared components for Read-Only by Default pattern
import useDetailViewLogic from '../../hooks/useDetailViewLogic';
import { DetailViewProvider } from '../../contexts/DetailViewContext';
import DetailViewActionBar from '../common/DetailViewActionBar';
import UnsavedChangesDialog from '../common/UnsavedChangesDialog';

// Import tab components
import UserOverviewTab from './UserDetail/UserOverviewTab';
// legacy UserSubscriptionsTab (multi-plan instances + token bonus)
// deleted. The new UserSubscriptionTab edits the 4-state machine directly.
import UserSubscriptionTab from './UserDetail/UserSubscriptionTab';
import UserAIAnalystTab from './UserDetail/UserAIAnalystTab';
import UserPlatformConnectionsTab from './UserDetail/UserPlatformConnectionsTab';
import UserRateLimitsTab from './UserDetail/UserRateLimitsTab';

// Tab name to index mapping (defined outside component to avoid dependency issues)
const TAB_NAMES = ['overview', 'subscription', 'platforms', 'ai-tasks', 'autopilot', 'json', 'audit', 'rate-limits'];
const getTabIndex = (tabName) => {
    const index = TAB_NAMES.indexOf(tabName);
    return index >= 0 ? index : 0;
};

const UserDetailView = () => {
    const { uid, tab } = useParams();
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    // lightweight blocked-state probe so the header badge is accurate
    // without opening the Rate Limits tab. The tab itself re-fetches full detail.
    const [rateLimitBlocked, setRateLimitBlocked] = useState(false);
    const [activeTab, setActiveTab] = useState(getTabIndex(tab));

    // probe blocked state on load for the header badge.
    useEffect(() => {
        let cancelled = false;
        adminApi.getUserRateLimits(uid)
            .then((data) => { if (!cancelled) setRateLimitBlocked(!!data?.blocked); })
            .catch(() => { /* non-fatal: badge simply stays hidden */ });
        return () => { cancelled = true; };
    }, [uid]);
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [archiving, setArchiving] = useState(false);

    // Hard-delete (irreversible erasure) — separate from archive.
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleteReason, setDeleteReason] = useState('');
    const [deleteResult, setDeleteResult] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    // subscriptionOptions / deletePlanDialog removed (legacy multi-plan-instance state).
    const [downloadingRecord, setDownloadingRecord] = useState(false);
    const [downloadingState, setDownloadingState] = useState(false);

    // State for unsaved changes dialog when switching tabs
    const [unsavedChangesDialog, setUnsavedChangesDialog] = useState({ open: false, pendingTab: null });

    // Normalize user data for the form (extract only editable fields)
    const normalizeUserData = useCallback((userData) => ({
        displayName: userData?.displayName || '',
        email: userData?.email || '',
        language: userData?.language || 'en'
    }), []);

    // Validate user data
    const validateUserData = useCallback((data) => {
        const errors = {};
        // displayName is optional, no validation needed
        // email is read-only, no validation needed
        // language has default, no validation needed
        return { isValid: Object.keys(errors).length === 0, errors };
    }, []);

    // Save user data
    const saveUserData = useCallback(async (data) => {
        const updated = await adminApi.updateUser(uid, data);
        setUser(updated);
        setSnackbar({ open: true, message: 'User updated successfully!', severity: 'success' });
    }, [uid]);

    // Use the standardized detail view logic hook
    const {
        isEditing,
        isSaving,
        hasUnsavedChanges,
        formData,
        error: editError,
        success: editSuccess,
        validationErrors,
        enterEditMode,
        handleSave,
        handleCancel,
        setFieldValue,
        updateFormData,
        clearMessages,
    } = useDetailViewLogic({
        initialData: user,
        onSave: saveUserData,
        onValidate: validateUserData,
        normalizeData: normalizeUserData,
    });

    // Sync tab from URL
    useEffect(() => {
        setActiveTab(getTabIndex(tab));
    }, [tab]);

    // Handle tab change - check for unsaved changes first
    const handleTabChange = useCallback((event, newValue) => {
        if (hasUnsavedChanges) {
            setUnsavedChangesDialog({ open: true, pendingTab: newValue });
        } else {
            setActiveTab(newValue);
            navigate(`/users/${uid}/${TAB_NAMES[newValue]}`, { replace: true });
        }
    }, [hasUnsavedChanges, uid, navigate]);

    // Handle unsaved changes dialog actions
    const handleUnsavedDiscard = useCallback(() => {
        handleCancel();
        const pendingTab = unsavedChangesDialog.pendingTab;
        setUnsavedChangesDialog({ open: false, pendingTab: null });
        setActiveTab(pendingTab);
        navigate(`/users/${uid}/${TAB_NAMES[pendingTab]}`, { replace: true });
    }, [handleCancel, unsavedChangesDialog.pendingTab, uid, navigate]);

    const handleUnsavedSaveAndContinue = useCallback(async () => {
        const success = await handleSave();
        if (success) {
            const pendingTab = unsavedChangesDialog.pendingTab;
            setUnsavedChangesDialog({ open: false, pendingTab: null });
            setActiveTab(pendingTab);
            navigate(`/users/${uid}/${TAB_NAMES[pendingTab]}`, { replace: true });
        }
    }, [handleSave, unsavedChangesDialog.pendingTab, uid, navigate]);

    const handleUnsavedCancel = useCallback(() => {
        setUnsavedChangesDialog({ open: false, pendingTab: null });
    }, []);

    const loadUser = useCallback(async () => {
        try {
            setLoading(true);
            setLoadError(null);

            // subscription options no longer fetched here — the new
            // UserSubscriptionTab pulls /admin/users/:uid/subscription directly.
            const userData = await adminApi.getUser(uid);
            setUser(userData);
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setLoadError(errorInfo);
            setSnackbar({ open: true, message: errorInfo.message, severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [uid]);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    const handleJsonSave = useCallback((newData) => {
        updateFormData(newData);
        setSnackbar({ open: true, message: 'JSON applied to form. Click Save to persist changes.', severity: 'info' });
    }, [updateFormData]);

    const handleArchiveUser = useCallback(async () => {
        setArchiving(true);
        try {
            await adminApi.archiveUser(uid);
            setSnackbar({ open: true, message: 'User archived successfully!', severity: 'success' });
            setArchiveDialogOpen(false);
            await loadUser(); // Reload to get updated status
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Archive failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setArchiving(false);
        }
    }, [uid, loadUser]);

    const handleRestoreUser = useCallback(async () => {
        setArchiving(true);
        try {
            await adminApi.restoreUser(uid);
            setSnackbar({ open: true, message: 'User restored successfully!', severity: 'success' });
            await loadUser(); // Reload to get updated status
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Restore failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setArchiving(false);
        }
    }, [uid, loadUser]);

    // Expected confirmation string for the hard-delete typed gate.
    const deleteConfirmTarget = user?.email || uid;
    const deleteConfirmValid = deleteConfirmText.trim() === (deleteConfirmTarget || '').trim();

    const openDeleteDialog = useCallback(() => {
        setDeleteConfirmText('');
        setDeleteReason('');
        setDeleteResult(null);
        setDeleteError(null);
        setDeleteDialogOpen(true);
    }, []);

    const handleDeleteUser = useCallback(async () => {
        if (!deleteConfirmValid) return;
        setDeleting(true);
        setDeleteError(null);
        try {
            const result = await adminApi.deleteUser(uid, {
                confirm: deleteConfirmText.trim(),
                reason: deleteReason.trim() || undefined,
            });
            if (result?.status === 'partial') {
                // Surface which steps failed; do not navigate away.
                setDeleteResult(result);
                setSnackbar({ open: true, message: 'User partially deleted — some steps failed. Review and retry.', severity: 'warning' });
            } else {
                setSnackbar({ open: true, message: 'User permanently deleted.', severity: 'success' });
                setDeleteDialogOpen(false);
                navigate('/users');
            }
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setDeleteError(errorInfo.message);
            setSnackbar({ open: true, message: `Delete failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setDeleting(false);
        }
    }, [uid, deleteConfirmText, deleteReason, deleteConfirmValid, navigate]);

    // legacy multi-plan-instance delete, plan-instance grant, and
    // token-bonus handlers removed. The new UserSubscriptionTab edits the
    // 4-state machine directly and the Credits sub-section there replaces
    // token-bonus entirely.

    const handleDownloadUserRecord = useCallback(async () => {
        setDownloadingRecord(true);
        try {
            const blob = await adminApi.exportUserRecord(uid);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `complete_user_${uid}_${new Date().toISOString()}.json`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setSnackbar({ open: true, message: 'User record downloaded successfully!', severity: 'success' });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Download failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setDownloadingRecord(false);
        }
    }, [uid]);

    const handleDownloadUserState = useCallback(async () => {
        setDownloadingState(true);
        try {
            const blob = await adminApi.exportUserState(uid);
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `full_user_state_${uid}_${new Date().toISOString()}.json`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setSnackbar({ open: true, message: 'Full user state downloaded successfully!', severity: 'success' });
        } catch (err) {
            const errorInfo = handleAdminApiError(err);
            setSnackbar({ open: true, message: `Download failed: ${errorInfo.message}`, severity: 'error' });
        } finally {
            setDownloadingState(false);
        }
    }, [uid]);

    const isDisabled = user?.status === 'disabled';

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (loadError && loadError.type === 'NOT_FOUND') {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">User not found</Alert>
                <Button startIcon={<ArrowBack />} onClick={() => navigate('/users')} sx={{ mt: 2 }}>
                    Back to Users
                </Button>
            </Box>
        );
    }

    return (
        <DetailViewProvider
            isEditing={isEditing}
            isSaving={isSaving}
            formData={formData}
            validationErrors={validationErrors}
            updateFormData={updateFormData}
            setFieldValue={setFieldValue}
        >
            <Box sx={{ p: 3 }} data-testid="user-detail-view">
                {/* Disabled User Banner */}
                {isDisabled && (
                    <Alert severity="warning" sx={{ mb: 3 }} data-testid="user-disabled-banner">
                        <Typography variant="subtitle2">This user is archived (disabled)</Typography>
                        <Typography variant="caption">
                            Archived on: {user?.archivedAt ? new Date(user.archivedAt).toLocaleString() : 'Unknown'}
                            {user?.archivedBy && ` by ${user.archivedBy}`}
                        </Typography>
                    </Alert>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Button startIcon={<ArrowBack />} onClick={() => navigate('/users')} sx={{ mr: 2 }} data-testid="user-back-btn">
                        Back
                    </Button>
                    <Typography variant="h4" data-testid="user-title">
                        {user?.email || uid}
                    </Typography>
                    {user?.isPremium && (
                        <Chip icon={<Star />} label="Premium" color="primary" sx={{ ml: 2 }} data-testid="user-premium-badge" />
                    )}
                    {isDisabled && (
                        <Chip label="Disabled" color="default" variant="outlined" sx={{ ml: 1 }} data-testid="user-disabled-badge" />
                    )}
                    {rateLimitBlocked && (
                        <Chip icon={<Block />} label="Blocked" color="error" sx={{ ml: 1 }} data-testid="user-blocked-badge" />
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    {/* Download Buttons */}
                    <Button
                        variant="outlined"
                        startIcon={downloadingRecord ? <CircularProgress size={20} /> : <DownloadIcon />}
                        onClick={handleDownloadUserRecord}
                        disabled={downloadingRecord}
                        sx={{ mr: 1 }}
                        data-testid="user-download-record-btn"
                    >
                        {downloadingRecord ? 'Downloading...' : 'User Record'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={downloadingState ? <CircularProgress size={20} /> : <DownloadIcon />}
                        onClick={handleDownloadUserState}
                        disabled={downloadingState}
                        sx={{ mr: 2 }}
                        data-testid="user-download-state-btn"
                    >
                        {downloadingState ? 'Downloading...' : 'Full State'}
                    </Button>
                    {/* Archive/Restore Button */}
                    {isDisabled ? (
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={archiving ? <CircularProgress size={20} /> : <RestoreFromTrash />}
                            onClick={handleRestoreUser}
                            disabled={archiving}
                            data-testid="user-restore-btn"
                        >
                            {archiving ? 'Restoring...' : 'Restore User'}
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<Archive />}
                            onClick={() => setArchiveDialogOpen(true)}
                            data-testid="user-archive-btn"
                        >
                            Archive User
                        </Button>
                    )}
                    {/* Danger zone: irreversible hard-delete (Firebase + all data). */}
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteForever />}
                        onClick={openDeleteDialog}
                        sx={{ ml: 1 }}
                        data-testid="user-delete-btn"
                    >
                        Delete
                    </Button>
                </Box>

                {/* Edit Action Bar - only show on Overview tab where editing is supported */}
                {activeTab === 0 && (
                    <Box sx={{ mb: 3 }}>
                        <DetailViewActionBar
                            isEditing={isEditing}
                            isSaving={isSaving}
                            hasUnsavedChanges={hasUnsavedChanges}
                            onEdit={enterEditMode}
                            onSave={handleSave}
                            onCancel={handleCancel}
                            error={editError}
                            success={editSuccess}
                            onClearMessages={clearMessages}
                            disabled={isDisabled}
                            entityName="User"
                        />
                    </Box>
                )}

                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                    data-testid="user-detail-tabs"
                >
                    <Tab label="Overview" icon={<Assessment />} iconPosition="start" data-testid="user-tab-overview" />
                    <Tab label="Subscription" icon={<SubscriptionIcon />} iconPosition="start" data-testid="user-tab-subscription" />
                    <Tab label="Platforms" icon={<PlatformsIcon />} iconPosition="start" data-testid="user-tab-platforms" />
                    <Tab label="AI Analyst" icon={<SmartToy />} iconPosition="start" data-testid="user-tab-ai-tasks" />
                    <Tab label="Autopilot" icon={<AutopilotIcon />} iconPosition="start" data-testid="user-tab-autopilot" />
                    <Tab label="JSON" icon={<DataObject />} iconPosition="start" data-testid="user-tab-json" />
                    <Tab label="Audit Logs" icon={<AuditIcon />} iconPosition="start" data-testid="user-tab-audit" />
                    <Tab label="Rate Limits" icon={<RateLimitIcon />} iconPosition="start" data-testid="user-tab-rate-limits" />
                </Tabs>

                {/* TAB 0: OVERVIEW */}
                {activeTab === 0 && (
                    <Card data-testid="user-overview-card">
                        <CardContent>
                            <UserOverviewTab
                                uid={uid}
                                user={user}
                                isDisabled={isDisabled}
                            />
                        </CardContent>
                    </Card>
                )}

                { /* TAB 1: SUBSCRIPTION (new 4-state model) */}
                {activeTab === 1 && (
                    <Card data-testid="user-subscription-card">
                        <CardContent>
                            <UserSubscriptionTab uid={uid} />
                        </CardContent>
                    </Card>
                )}

                { /* TAB 2: PLATFORMS */}
                {activeTab === 2 && (
                    <Card data-testid="user-platforms-card">
                        <CardContent>
                            <UserPlatformConnectionsTab uid={uid} />
                        </CardContent>
                    </Card>
                )}

                {/* TAB 3: AI ANALYST */}
                {activeTab === 3 && (
                    <Card data-testid="user-ai-tasks-card">
                        <CardContent>
                            <UserAIAnalystTab uid={uid} />
                        </CardContent>
                    </Card>
                )}

                { /* TAB 4: AUTOPILOT */}
                {activeTab === 4 && (
                    <Card data-testid="user-autopilot-card">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Autopilot</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Autopilot configuration summary for this user.
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={() => navigate(`/autopilot/${uid}`)}
                                data-testid="user-autopilot-link"
                            >
                                Open Full Autopilot Dashboard &rarr;
                            </Button>
                        </CardContent>
                    </Card>
                )}

                {/* TAB 5: JSON */}
                {activeTab === 5 && (
                    <Card elevation={0} variant="outlined" data-testid="user-json-card">
                        <JsonViewTab
                            data={{
                                uid,
                                ...user, // include read-only fields from backend
                                ...formData // overwrite with local edits
                            }}
                            onSave={handleJsonSave}
                            disabled={!isEditing}
                        />
                    </Card>
                )}

                {/* TAB 6: AUDIT LOGS */}
                {activeTab === 6 && (
                    <Card elevation={0} variant="outlined" data-testid="user-audit-card">
                        <AuditLogView defaultFilters={{ uid }} />
                    </Card>
                )}

                { /* TAB 7: RATE LIMITS */}
                {activeTab === 7 && (
                    <Card data-testid="user-rate-limits-card">
                        <CardContent>
                            <UserRateLimitsTab uid={uid} onStatusChange={(s) => setRateLimitBlocked(!!s?.blocked)} />
                        </CardContent>
                    </Card>
                )}

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    message={snackbar.message}
                />

                {/* Archive Confirmation Dialog */}
                <Dialog
                    open={archiveDialogOpen}
                    onClose={() => setArchiveDialogOpen(false)}
                    data-testid="user-archive-dialog"
                >
                    <DialogTitle>Archive User?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to archive this user? The user will be marked as disabled
                            and hidden from the default user list. All user data will be preserved and can
                            be restored later.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setArchiveDialogOpen(false)} data-testid="user-archive-cancel-btn">Cancel</Button>
                        <Button
                            onClick={handleArchiveUser}
                            color="error"
                            variant="contained"
                            disabled={archiving}
                            startIcon={archiving ? <CircularProgress size={16} /> : null}
                            data-testid="user-archive-confirm-btn"
                        >
                            {archiving ? 'Archiving...' : 'Archive'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Hard-Delete (irreversible erasure) Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => !deleting && setDeleteDialogOpen(false)}
                    data-testid="user-delete-dialog"
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ color: 'error.main' }}>Permanently delete this user?</DialogTitle>
                    <DialogContent>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            This is <strong>irreversible</strong>. It cannot be undone — use <strong>Archive</strong> instead
                            if you only need to disable the account.
                        </Alert>
                        <DialogContentText component="div" sx={{ mb: 2 }}>
                            This will erase:
                            <ul style={{ marginTop: 4, marginBottom: 0 }}>
                                <li>the user's <strong>Firebase login</strong> (they can no longer sign in)</li>
                                <li>all campaigns, ad groups, keywords, ads &amp; sites</li>
                                <li>connected <strong>Google / Microsoft Ads</strong> accounts (tokens revoked)</li>
                                <li>API tokens, subscription, credits, usage &amp; spaces</li>
                            </ul>
                        </DialogContentText>

                        {deleteResult?.status === 'partial' && (
                            <Alert severity="warning" sx={{ mb: 2 }} data-testid="user-delete-partial">
                                Partial deletion — some steps failed:
                                <ul style={{ marginTop: 4, marginBottom: 0 }}>
                                    {deleteResult.steps?.filter((s) => !s.ok && !s.skipped).map((s) => (
                                        <li key={s.name}>{s.name}: {s.error || 'failed'}</li>
                                    ))}
                                </ul>
                                You can retry — the operation is idempotent.
                            </Alert>
                        )}
                        {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}

                        <DialogContentText sx={{ mb: 1 }}>
                            Type <strong>{deleteConfirmTarget}</strong> to confirm:
                        </DialogContentText>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={deleteConfirmTarget}
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            error={deleteConfirmText.length > 0 && !deleteConfirmValid}
                            disabled={deleting}
                            sx={{ mb: 2 }}
                            data-testid="user-delete-confirm-input"
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Reason (optional — compliance note)"
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                            disabled={deleting}
                            data-testid="user-delete-reason-input"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting} data-testid="user-delete-cancel-btn">Cancel</Button>
                        <Button
                            onClick={handleDeleteUser}
                            color="error"
                            variant="contained"
                            disabled={!deleteConfirmValid || deleting}
                            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteForever />}
                            data-testid="user-delete-confirm-btn"
                        >
                            {deleting ? 'Deleting...' : 'Delete permanently'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Plan Instance dialog removed — the new
                    UserSubscriptionTab edits state via PUT /admin/users/:uid/subscription/state. */}

                {/* Unsaved Changes Dialog */}
                <UnsavedChangesDialog
                    open={unsavedChangesDialog.open}
                    onDiscard={handleUnsavedDiscard}
                    onSaveAndContinue={handleUnsavedSaveAndContinue}
                    onCancel={handleUnsavedCancel}
                    isSaving={isSaving}
                    action="switch tabs"
                />
            </Box>
        </DetailViewProvider>
    );
};

export default UserDetailView;
